import React, { useEffect, useState } from 'react';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import OrderItemsDetail from '../components/OrderItemsDetail/OrderItemsDetail';
import { formatDate, formatPrice, formatItemsSummary, getNextOrderStatus, getNextStatusActionLabel, getStatusLabel, orderNeedsTracking } from '../utils/orderUtils';
import { formatShippingAddressLines } from '../utils/addressUtils';
import { LOCAL_DELIVERY_BANNER } from '../constants/localDelivery';
import { notifyStatusUpdate, refundOrder } from '../services/notifyApi';
import { useInventory } from '../contexts/InventoryContext';
import { isAdminUser } from '../utils/adminUtils';
import './AdminPage.css';

const STATUS_LABELS = {
  pago: 'Pago',
  processando: 'Em processamento',
  enviado: 'Enviado',
  entregue: 'Entregue',
  cancelado: 'Cancelado',
};

const getOrderStatusLabel = (order) =>
  getStatusLabel(order.status, order.localDelivery);

const CANCELLABLE_STATUSES = ['pago', 'processando'];

const AdminPage = () => {
  const { currentUser } = useAuth();
  const { incrementStock } = useInventory();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('todos');
  const [expandedId, setExpandedId] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});
  const [statusErrors, setStatusErrors] = useState({});

  const isAdmin = isAdminUser(currentUser);

  useEffect(() => {
    if (!currentUser) { navigate('/'); return; }
    if (!isAdmin) { navigate('/'); return; }

    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'orders'));
        const fetched = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        fetched.sort((a, b) => {
          const aTime = a.createdAt?.toDate?.() || new Date(a.createdAt || 0);
          const bTime = b.createdAt?.toDate?.() || new Date(b.createdAt || 0);
          return bTime - aTime;
        });
        setOrders(fetched);
      } catch (err) {
        console.error('Erro ao carregar pedidos:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [currentUser, isAdmin, navigate]);

  const updateStatus = async (order, newStatus) => {
    setStatusErrors((prev) => ({ ...prev, [order.id]: null }));
    const trackingCode = trackingInputs[order.id]?.trim() || order.trackingCode || '';

    if (newStatus === 'enviado' && !order.localDelivery && !trackingCode) {
      setStatusErrors((prev) => ({
        ...prev,
        [order.id]: 'Informe o código de rastreio dos Correios antes de marcar como enviado.',
      }));
      return;
    }

    setUpdating(order.id);
    try {
      const updates = { status: newStatus };
      if (newStatus === 'enviado') {
        updates.trackingCode = trackingCode;
        updates.shippedAt = serverTimestamp();
      }

      await updateDoc(doc(db, 'orders', order.id), updates);

      const updatedOrder = { ...order, ...updates, trackingCode };
      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? updatedOrder : o))
      );

      await notifyStatusUpdate({
        adminEmail: currentUser.email,
        order: updatedOrder,
        status: newStatus,
        trackingCode,
        localDelivery: order.localDelivery,
      });
    } catch (err) {
      setStatusErrors((prev) => ({
        ...prev,
        [order.id]: err.message || 'Erro ao atualizar pedido.',
      }));
      console.error('Erro ao atualizar status:', err);
    } finally {
      setUpdating(null);
    }
  };

  const cancelOrder = async (order) => {
    const customer = order.customerName || order.customerEmail || 'o cliente';
    const confirmed = window.confirm(
      `Cancelar e reembolsar ${formatPrice(order.total)} para ${customer}?\n\nO estoque será restaurado e o cliente receberá um email.`
    );
    if (!confirmed) return;

    setStatusErrors((prev) => ({ ...prev, [order.id]: null }));
    setUpdating(order.id);

    try {
      const result = await refundOrder({ adminEmail: currentUser.email, order });

      const updates = {
        status: 'cancelado',
        cancelledAt: serverTimestamp(),
        refundId: result.refundId,
      };

      await updateDoc(doc(db, 'orders', order.id), updates);

      const itemsToRestore = (order.items || [])
        .filter((item) => item.productId && item.size)
        .map((item) => ({
          productId: item.productId,
          size: item.size,
          quantity: item.quantity || 1,
        }));

      if (itemsToRestore.length > 0) {
        await incrementStock(itemsToRestore);
      }

      setOrders((prev) =>
        prev.map((o) => (o.id === order.id ? { ...o, ...updates, status: 'cancelado' } : o))
      );
    } catch (err) {
      setStatusErrors((prev) => ({
        ...prev,
        [order.id]: err.message || 'Erro ao cancelar pedido.',
      }));
      console.error('Erro ao cancelar pedido:', err);
    } finally {
      setUpdating(null);
    }
  };

  const filtered = filterStatus === 'todos'
    ? orders
    : orders.filter((o) => o.status === filterStatus);

  if (!currentUser || !isAdmin) return null;

  return (
    <div className="admin-page">
      <div className="admin-container">
        <div className="admin-header">
          <h1 className="admin-title">Pedidos</h1>
          <span className="admin-count">{filtered.length} pedido{filtered.length !== 1 ? 's' : ''}</span>
        </div>

        <div className="admin-filters">
          {['todos', 'pago', 'processando', 'enviado', 'entregue', 'cancelado'].map((s) => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`admin-filter-btn ${filterStatus === s ? 'active' : ''}`}
            >
              {s === 'todos' ? 'Todos' : STATUS_LABELS[s]}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="admin-loading">Carregando pedidos...</div>
        ) : filtered.length === 0 ? (
          <div className="admin-empty">Nenhum pedido encontrado.</div>
        ) : (
          <div className="admin-orders">
            {filtered.map((order) => {
              const nextStatus = getNextOrderStatus(order);
              const needsTracking = orderNeedsTracking(order, nextStatus);
              const canCancel = CANCELLABLE_STATUSES.includes(order.status);

              return (
                <div key={order.id} className="admin-order-card">
                  <div
                    className="admin-order-summary"
                    onClick={() => setExpandedId(expandedId === order.id ? null : order.id)}
                  >
                    <div className="admin-order-col">
                      <span className="admin-label">Data</span>
                      <span>{formatDate(order.createdAt)}</span>
                    </div>
                    <div className="admin-order-col">
                      <span className="admin-label">Cliente</span>
                      <span>{order.customerName || order.customerEmail || '—'}</span>
                    </div>
                    <div className="admin-order-col">
                      <span className="admin-label">Itens</span>
                      <span>{formatItemsSummary(order.items)}</span>
                    </div>
                    <div className="admin-order-col">
                      <span className="admin-label">Total</span>
                      <span className="admin-total">{formatPrice(order.total)}</span>
                    </div>
                    <div className="admin-order-col">
                      <span className="admin-label">Status</span>
                      <span className={`admin-status admin-status--${order.status}`}>
                        {getOrderStatusLabel(order)}
                      </span>
                    </div>
                    <span className="admin-order-toggle">{expandedId === order.id ? '▲' : '▼'}</span>
                  </div>

                  {expandedId === order.id && (
                    <div className="admin-order-detail">
                      {order.localDelivery && (
                        <div className="admin-local-delivery-banner">
                          {LOCAL_DELIVERY_BANNER}
                        </div>
                      )}
                      <div className="admin-detail-grid">
                        <div>
                          <h4 className="admin-detail-title">Itens do pedido</h4>
                          <OrderItemsDetail
                            items={order.items}
                            shippingService={order.shippingService}
                            shippingCost={order.shippingCost}
                            total={order.total}
                          />
                        </div>

                        <div>
                          <h4 className="admin-detail-title">Endereço de Entrega</h4>
                          {(() => {
                            const addressLines = formatShippingAddressLines(order.shippingAddress);
                            return addressLines ? (
                              <div className="admin-address">
                                {addressLines.map((line) => (
                                  <p key={line}>{line}</p>
                                ))}
                              </div>
                            ) : (
                              <p className="admin-no-address">Endereço não disponível</p>
                            );
                          })()}
                          {order.customerPhone && (
                            <p className="admin-phone">Tel: {order.customerPhone}</p>
                          )}
                          <p className="admin-email">{order.customerEmail}</p>

                          {order.trackingCode && (
                            <div className="admin-tracking">
                              <h4 className="admin-detail-title">Rastreio</h4>
                              <a
                                href={`https://rastreamento.correios.com.br/app/index.php?objeto=${order.trackingCode}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="admin-tracking-link"
                              >
                                {order.trackingCode} — Rastrear nos Correios
                              </a>
                            </div>
                          )}

                          {order.status === 'cancelado' && (
                            <div className="admin-cancelled-info">
                              <h4 className="admin-detail-title">Cancelamento</h4>
                              <p>Pedido cancelado e reembolso iniciado.</p>
                              {order.refundId && (
                                <p className="admin-refund-id">Reembolso: {order.refundId}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {statusErrors[order.id] && updating !== order.id && (
                        <p className="admin-status-error">{statusErrors[order.id]}</p>
                      )}

                      {needsTracking && (
                        <div className="admin-tracking-input-group">
                          <label htmlFor={`tracking-${order.id}`} className="admin-tracking-label">
                            Código de rastreio (Correios)
                          </label>
                          <input
                            id={`tracking-${order.id}`}
                            type="text"
                            placeholder="Ex: AA123456789BR"
                            value={trackingInputs[order.id] ?? order.trackingCode ?? ''}
                            onChange={(e) =>
                              setTrackingInputs((prev) => ({ ...prev, [order.id]: e.target.value }))
                            }
                            className="admin-tracking-input"
                          />
                          <p className="admin-tracking-hint">
                            O cliente receberá este código por email ao marcar como enviado.
                            Para testar, use um código fictício como AA123456789BR.
                          </p>
                        </div>
                      )}

                      <div className="admin-status-actions">
                        {canCancel && (
                          <button
                            className="btn admin-cancel-btn"
                            disabled={updating === order.id}
                            onClick={() => cancelOrder(order)}
                          >
                            <span>
                              {updating === order.id
                                ? 'Processando...'
                                : 'Cancelar e reembolsar'}
                            </span>
                          </button>
                        )}

                        {nextStatus && (
                          <button
                            className="btn btn-filled admin-status-btn"
                            disabled={updating === order.id}
                            onClick={() => updateStatus(order, nextStatus)}
                          >
                            <span>
                              {updating === order.id
                                ? 'Atualizando...'
                                : `Marcar como "${getNextStatusActionLabel(order)}"`}
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPage;
